import React from 'react';
import RecommendationScore from './RecommendationScore';
import RecommendationReasons from './RecommendationReasons';
import { CANDIDATE_VISIBLE_LABEL, NOT_PROPOSED_BADGE } from './candidateVisibility';
import { MISSING_FROM_POOL_NOTE, MISSING_FROM_POOL_TITLE } from './jobPoolDeletion';
import './JobPoolScope.css';

// マッチ度の編集の状態を渡されないとき (編集の操作を使わない呼び出し元) の既定
const NO_SCORE_EDIT = {};
// おすすめポイントの文の編集の状態を渡されないときの既定 (同じ。Issue #777)
const NO_REASONS_EDIT = {};

/**
 * 推薦候補カード (SCR-M-001、DP-M-003〜005)
 *
 * 4 セクション構成: 企業名・ポジション / 特徴 / 仕事内容 / おすすめポイント
 *
 * 並べ替え (FR-MATCH-003) は D&D (Issue #596) + ↑↓ボタンの両対応。D&D は HTML5 標準の
 * Drag and Drop API を使用し (追加ライブラリ不要)、カード全体を draggable にする。
 * ネイティブ D&D はタッチ操作に対応しないため、タブレット等では自然と↑↓ボタンにフォール
 * バックする (設計書「タブレット768〜1024pxは並べ替えは↑↓ボタン優先」と整合)。
 */
function RecommendationCard({
    item,
    index,
    isFirst,
    isLast,
    onMoveUp,
    onMoveDown,
    onOpenDetail,
    onDragStart,
    onDragOverCard,
    onDropCard,
    onDragEnd,
    isDragOver,
    scoreHidden = false,
    scoreEdit = NO_SCORE_EDIT,
    reasonsEdit = NO_REASONS_EDIT,
    candidateVisibility = null,
    missingFromPool = false,
    displayRank = null,
    recommended = false,
}) {
    const job = item.jobPoolItem;
    // 求職者に提案するか (IN-M-011・DP-M-018。SCR-M-001 §4.4、Issue #992)。渡されない呼び出し元では
    // チェックボックスも印も出さない。visible は保存済みの設定 (印と枠に使う)、checked はチェックの表示
    // (保存中は切り替えた後の状態)
    const notProposed = candidateVisibility !== null && !candidateVisibility.visible;
    // マッチ度の値の読み方 (BE は 0〜100 のスケールで返す。Issue #537) は RecommendationScore・scoreOverride.js

    if (missingFromPool) {
        // 求人プールに無い求人の枠 (SCR-M-001 §4.4、Issue #883)。一覧の取得には成功したのに、推薦された求人が
        // 一覧に無いとき (削除された・置き換えで無効になった等)。番号・並べ替え・マッチ度・IN-M-011・
        // 「詳細を見る」・企業名を出さない (推薦結果に名前を持っておらず、消した求人の名前を残さないため)
        return (
            <div className="recommendation-missing-from-pool">
                <p className="recommendation-missing-from-pool-title">{MISSING_FROM_POOL_TITLE}</p>
                <p className="recommendation-missing-from-pool-note">{MISSING_FROM_POOL_NOTE}</p>
            </div>
        );
    }

    if (!job) {
        // 推薦された求人の情報が一覧に無く、一覧の取得にも失敗しているときのフォールバック (再読み込みで直る)。
        // 内部 ID は利用者に意味が無いため表示しない (画面レイアウト_SCR-M-001.md §4.4・§6.3、Issue #881)
        return (
            <div className="recommendation-card recommendation-card--missing">
                <p className="matching-filter-empty">
                    求人の情報を取得できませんでした。ページを再読み込みしてください。
                </p>
            </div>
        );
    }

    const handleDragStart = (event) => {
        onDragStart(item.job_pool_item_id);
        // Firefox 等一部ブラウザは dataTransfer にデータをセットしないと dragstart が
        // 発火しないため、実際には未使用だが最小限のデータを設定しておく
        event.dataTransfer?.setData('text/plain', item.job_pool_item_id);
        event.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (event) => {
        event.preventDefault(); // これが無いと onDrop が発火しない
        event.dataTransfer.dropEffect = 'move';
        onDragOverCard(index);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        onDropCard(index);
    };

    return (
        <div
            // 載せない候補は枠を破線にする (色だけでなく線の種類で分ける)。ドラッグ中のドロップ先の表示は
            // 破線より優先する (CSS の並びで後に書く。SCR-M-001 §4.4)
            className={
                'recommendation-card'
                + (notProposed ? ' recommendation-card--not-proposed' : '')
                + (isDragOver ? ' recommendation-card--drag-over' : '')
            }
            // マッチ度の編集中はドラッグできなくする (入力欄の中でマウスを動かしたときに、文字の選択ではなく
            // カードの移動が始まらないようにするため。↑↓ ボタンは使える。SCR-M-001 §6.2、Issue #1435)
            // おすすめポイントの文の編集中も同じ (SCR-M-001 §6.2「おすすめポイントの文の編集」手順 2、Issue #777)
            draggable={scoreEdit.editing || reasonsEdit.editing ? 'false' : 'true'}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragEnd={onDragEnd}
        >
            <div className="recommendation-card-header">
                <span
                    className="recommendation-drag-handle"
                    aria-hidden="true"
                    title="ドラッグして並べ替え"
                >
                    ⠿
                </span>
                <span className="recommendation-rank">{displayRank ?? item.rank}</span>
                <div className="recommendation-reorder-buttons">
                    <button
                        type="button"
                        className="btn-row btn-row--ghost"
                        onClick={() => onMoveUp(item.job_pool_item_id)}
                        disabled={isFirst}
                        aria-label="1つ上に移動"
                    >
                        ↑
                    </button>
                    <button
                        type="button"
                        className="btn-row btn-row--ghost"
                        onClick={() => onMoveDown(item.job_pool_item_id)}
                        disabled={isLast}
                        aria-label="1つ下に移動"
                    >
                        ↓
                    </button>
                </div>
                {/* DP-M-018: 載せない候補の印。順位・並べ替えの部品の右 (SCR-M-001 §4.2、Issue #992) */}
                {notProposed && <span className="recommendation-not-proposed-badge">{NOT_PROPOSED_BADGE}</span>}
                {/* DP-M-020: 「おすすめ」の印。DP-M-018 の右。枠・取得失敗のカードには出さない (上で返している。
                    SCR-M-001 §4.2・§4.6.6、Issue #782) */}
                {recommended && <span className="job-pool-recommended-mark">おすすめ</span>}
                {/* DP-M-004 マッチ度と、その数字の書き換え (IN-M-010)。「マッチ度を表示する」(IN-M-009) を
                    外している間は出さない (推薦 PDF にも出ない。SCR-M-001 §4.4、Issue #1435) */}
                <RecommendationScore item={item} scoreHidden={scoreHidden} {...scoreEdit} />
            </div>

            {/* セクション1: 企業名・ポジション */}
            <div className="recommendation-section">
                <h4 className="recommendation-company" id={companyHeadingId(item.job_pool_item_id)}>
                    {job.company_name}
                </h4>
                <p className="recommendation-title">{job.job_title}</p>
                <p className="recommendation-meta">
                    {job.work_location && <span>{job.work_location}</span>}
                    {job.employment_type && <span>{job.employment_type}</span>}
                    {(job.salary_min || job.salary_max) && (
                        <span>
                            年収 {job.salary_min ?? '?'}〜{job.salary_max ?? '?'}万円
                        </span>
                    )}
                </p>
            </div>

            {/* セクション2: 特徴 */}
            {(job.features_description || (job.skills && job.skills.length > 0)) && (
                <div className="recommendation-section">
                    <h5 className="recommendation-section-title">特徴</h5>
                    {job.features_description && <p>{job.features_description}</p>}
                    {job.skills && job.skills.length > 0 && (
                        <div className="recommendation-skill-chips">
                            {job.skills.map((skill) => (
                                <span key={skill} className="recommendation-chip">{skill}</span>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* セクション3: 仕事内容 */}
            {(job.job_description_summary || job.job_description) && (
                <div className="recommendation-section">
                    <h5 className="recommendation-section-title">仕事内容</h5>
                    <p className="recommendation-job-description">
                        {job.job_description_summary || job.job_description}
                    </p>
                </div>
            )}

            {/* セクション4: おすすめポイント (DP-M-005) と、その文の編集 (IN-M-012。SCR-M-001 §4.4、Issue #777) */}
            <RecommendationReasons item={item} {...reasonsEdit} />

            {/* フッタ: 左に IN-M-011、右に「詳細を見る」(BTN-M-006)。収まらないときは折り返す (SCR-M-001 §4.4・§7.3) */}
            <div className="recommendation-card-footer">
                {candidateVisibility !== null && (
                    <CandidateVisibilityControl
                        itemId={item.job_pool_item_id}
                        candidateVisibility={candidateVisibility}
                    />
                )}
                <button
                    type="button"
                    className="modal-button-cancel recommendation-detail-button"
                    onClick={() => onOpenDetail(job)}
                >
                    詳細を見る
                </button>
            </div>
        </div>
    );
}

// カードの企業名の見出しの ID (IN-M-011 の読み上げで、どの求人のチェックかを結ぶ。§7.2)
function companyHeadingId(jobPoolItemId) {
    return `recommendation-company-${jobPoolItemId}`;
}

/**
 * IN-M-011「求職者に提案する（PDF に載せる）」(SCR-M-001 §4.1・§6.2・§7.2、Issue #992)。
 * 文言を押しても切り替わるよう <label> で包む。全カードで同じ文言のため、aria-describedby で
 * そのカードの企業名と結ぶ。保存中は押せず「保存中...」、失敗はチェックを戻して文言を出す。
 */
function CandidateVisibilityControl({ itemId, candidateVisibility }) {
    const { checked, saving, locked, errorMessage, onChange } = candidateVisibility;
    return (
        <div className="recommendation-candidate-visibility">
            <label className="recommendation-candidate-visibility-label">
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={(event) => onChange(event.target.checked)}
                    disabled={saving || locked}
                    aria-describedby={companyHeadingId(itemId)}
                />
                {CANDIDATE_VISIBLE_LABEL}
            </label>
            {/* 領域ごと出し入れすると読み上げられないことがあるため、role="status" は常に置く */}
            <span role="status" className="recommendation-candidate-visibility-status">
                {saving && '保存中...'}
            </span>
            {errorMessage && (
                <span className="recommendation-candidate-visibility-error" role="alert">{errorMessage}</span>
            )}
        </div>
    );
}

export default RecommendationCard;
